import React from "react";
import { ColumnDef, Row, Cell } from "@tanstack/react-table";
import { EditableInputType } from "./EditableCell";
import { SortFieldType } from "./sortUtils";

export interface BuildEditableCellColumnProps<T> {
  accessorKey: string;
  header: string;
  onEdit: (value: string, row: T) => void;
  editable?: boolean | ((row: T) => boolean);
  editableInputType?: EditableInputType;
  getIcon?: (row: T) => React.ReactNode;
  size?: number;
  minSize?: number;
  enableSorting?: boolean;
  className?: string;
  editableClassName?: string;
  sortType?: SortFieldType;
  meta?: Record<string, any>;
}

/**
 * Helper to create an inline-editable text or currency column.
 *
 * Wraps the existing `EditableCell` column meta configuration into a clean builder,
 * eliminating boilerplate for `meta.editable`, `meta.onEdit`, `meta.editableInputType`,
 * `meta.editableIcon`, and `meta.editableClassName`.
 *
 * @example
 * ```tsx
 * buildEditableCellColumn<RegistrantRow>({
 *   accessorKey: "score_18_1",
 *   header: "Round 1",
 *   onEdit: (value, row) => onScoreUpdate("roundOneScore", row.id, value),
 *   editable: (row) => !isFinalized && row.attended !== false,
 *   getIcon: (row) => row.meets_qualifying_score ? <Icon name="check" /> : null,
 *   size: 80
 * })
 * ```
 */
export function buildEditableCellColumn<T>({
  accessorKey,
  header,
  onEdit,
  editable = true,
  editableInputType = "default",
  getIcon,
  size,
  minSize,
  enableSorting,
  className,
  editableClassName,
  sortType,
  meta
}: BuildEditableCellColumnProps<T>): ColumnDef<T> {
  return {
    accessorKey,
    header,
    ...(size && { size }),
    ...(minSize && { minSize }),
    ...(enableSorting !== undefined && { enableSorting }),
    meta: {
      editable:
        typeof editable === "function" ? (row: Row<T>) => (editable as (row: T) => boolean)(row.original) : editable,
      onEdit: (value: string, cell: Cell<T, unknown>) => onEdit(value, cell.row.original),
      editableInputType,
      ...(getIcon && {
        editableIcon: (cell: Cell<T, unknown>) => getIcon(cell.row.original)
      }),
      ...(className && { className }),
      ...(editableClassName && { editableClassName }),
      ...(sortType && { sortType }),
      ...meta
    }
  };
}
